import React, { useEffect, useRef, useState } from "react";
import styles from "./CustomerDetailsCard.module.scss";
import { message, Popover, Rate } from "antd";
import fallbackImage from "../../../../assets/images/image-fallback.svg";
import notesIcon from "../../../../assets/images/notes-icon.svg";
import ellipsisIcon from "../../../../assets/images/more-ellipsis.svg";
import locationIcon from "../../../../assets/images/grey-marker.svg";
import mailIcon from "../../../../assets/images/email-outline.svg";
import phoneIcon from "../../../../assets/images/phone-outline.svg";
import clockIcon from "../../../../assets/images/clock-outline.svg";
import downArrow from "../../../../assets/images/dropdown-arrow.svg";
import ConditionalRender from "../../../../reusable-components/ConditionalRender";
import {
  CustomerDetailConsts,
  getFormattedTimes,
  getGoogleMapsUrl,
  getMoreOptions,
  getIsClosedDay,
  getTodaysDateTime,
} from "./constants";
import NotesModal from "./NotesModal";
import Form from "../../../Form";
import { PopoverTrigger } from "../../../../enums/PopoverEnums";
import { PlacementPositions } from "../../../../enums/PlacementPositionsEnums";
import clsx from "clsx";
import { CustomerProfileDetailsType } from "../../../../Types/customerDatabaseTypes";
import moment from "moment";

interface CustomerDetailsCardProps {
  customerDetails: CustomerProfileDetailsType;
  handleEditFormOpen: () => void;
}

const CustomerDetailsCard = ({
  customerDetails,
  handleEditFormOpen,
}: CustomerDetailsCardProps) => {
  const {
    MORE_PHOTOS,
    EDIT,
    WEBSITE,
    OPEN,
    OPENS_AT,
    CLOSED,
    CLOSES_AT,
    RATE_STAR_SIZE,
    NOTES_ALT,
    REDIRECT_TARGET,
    REL_VALUE,
    NO_WEBSITE_MESSAGE,
  } = CustomerDetailConsts;

  const [currentTime, setCurrentTime] = useState(moment());
  const [notesModalOpen, setNotesModalOpen] = useState<boolean>(false);
  
  // Recalculate based on current time
  const isClosedDay = getIsClosedDay(customerDetails?.openingHours);
  const hoursItems = getFormattedTimes(customerDetails?.openingHours);
  const dateTimeValues = getTodaysDateTime(customerDetails?.openingHours);
  
  const handleNotesModalOpen = () => setNotesModalOpen(true);
  const handleNotesModalClose = () => setNotesModalOpen(false);

  const handleNavigateWebsite = (url: string) => {
    if (url) window.open(url, REDIRECT_TARGET, REL_VALUE);
    else message.info(NO_WEBSITE_MESSAGE);
  };

  const handleNavigateToAddressLocation = () => {
    window.open(
      getGoogleMapsUrl(customerDetails?.lat, customerDetails?.lng),
      REDIRECT_TARGET,
      REL_VALUE,
    );
  };

  useEffect(() => {
    const now = moment();
    const timeouts: NodeJS.Timeout[] = [];
    
    if (dateTimeValues?.open && dateTimeValues?.close) {
      if (now.isBefore(dateTimeValues.open)) {
        const msUntilOpen = dateTimeValues.open.diff(now);
        timeouts.push(setTimeout(() => setCurrentTime(moment()), msUntilOpen));
      }
      
      if (now.isBefore(dateTimeValues.close)) {
        const msUntilClose = dateTimeValues.close.diff(now);
        timeouts.push(setTimeout(() => setCurrentTime(moment()), msUntilClose));
      }
    }
    
    const tomorrow = moment().add(1, 'day').startOf('day');
    const msUntilMidnight = tomorrow.diff(now);
    timeouts.push(setTimeout(() => setCurrentTime(moment()), msUntilMidnight));

    return () => {
      timeouts.forEach(timeout => clearTimeout(timeout));
    };
  }, [currentTime, customerDetails?.openingHours]);

  return (
    <>
      <Form>
        {() => (
          <NotesModal
            isOpen={notesModalOpen}
            handleClose={handleNotesModalClose}
            customerId={customerDetails?.id}
            customerName={customerDetails?.companyName}
            notesValue={customerDetails?.notes}
          />
        )}
      </Form>
      <div className={styles.customerCard}>
        <div className={styles.topSection}>
          <div className={styles.imageHolder}>
            <ConditionalRender
              visible={!!customerDetails?.photoURL}
              fallback={<img src={fallbackImage} />}
            >
              <div className={styles.imageContainer}>
                <img
                  className={styles.customerImage}
                  src={customerDetails?.photoURL}
                />
                <div
                  onClick={() =>
                    handleNavigateWebsite(customerDetails?.redirectLink)
                  }
                  className={styles.morePhotosButton}
                >
                  {MORE_PHOTOS}
                </div>
              </div>
            </ConditionalRender>
          </div>

          <div className={styles.customerDetails}>
            <div className={styles.customerName}>
              {customerDetails?.companyName}
            </div>
            <div className={styles.customerRating}>
              <div className={styles.ratingText}>
                {Number?.isFinite(customerDetails?.rating) && customerDetails?.rating?.toFixed(1)}
              </div>
              <Rate
                disabled
                value={Number?.isFinite(customerDetails?.rating) ? customerDetails?.rating : undefined}
                allowHalf
                style={RATE_STAR_SIZE}
              />
            </div>
            <ConditionalRender visible={!!customerDetails?.address}>
              <div className={styles.customerInfo}>
                <img src={locationIcon} />
                <a
                  className={styles.locationName}
                  onClick={() => handleNavigateToAddressLocation()}
                >
                  {customerDetails?.address}
                </a>
              </div>
            </ConditionalRender>
            <ConditionalRender visible={!!customerDetails?.email}>
              <div className={styles.customerInfo}>
                <img src={mailIcon} />
                <span className={styles.emailText}>{customerDetails?.email}</span>
              </div>
            </ConditionalRender>
            <ConditionalRender visible={!!customerDetails?.phone}>
              <div className={styles.customerInfo}>
                <img src={phoneIcon} />
                <span className={styles.numberText}>
                  {customerDetails?.phone}
                </span>
              </div>
            </ConditionalRender>
            <ConditionalRender visible={!!customerDetails?.openingHours?.length}>
              <div className={styles.customerInfo}>
                <img src={clockIcon} />
                <div className={styles.openHoursDetails}>
                    <span
                    className={clsx({
                      [styles.closeStatus]: !dateTimeValues?.isOpen,
                      [styles.openStatus]: dateTimeValues?.isOpen,
                    })}
                  >
                    {dateTimeValues?.isOpen ? OPEN : CLOSED}
                  </span>
                  <span>
                    {dateTimeValues?.isOpen
                      ? dateTimeValues?.closesAt
                      : dateTimeValues?.opensAt}
                  </span>
                </div>
                <Popover
                  trigger={PopoverTrigger.HOVER}
                  content={
                    <div className={styles.hoursContainer}>
                      <div className={styles.hoursWrapper}>
                        {hoursItems?.map((item, index) => (
                          <div key={`day-${index}`} className={styles.timeStatus}>
                            {item?.[0]}
                          </div>
                        ))}
                      </div>
                      <div className={styles.hoursWrapper}>
                        {hoursItems?.map((item, index) => (
                          <div key={`time-${index}`} className={styles.timeStatus}>
                            {item?.[1]}
                          </div>
                        ))}
                      </div>
                    </div>
                  }
                  placement={PlacementPositions.BOTTOM}
                >
                  <div>
                    <img src={downArrow} />
                  </div>
                </Popover>
              </div>
            </ConditionalRender>
          </div>
          <div className={styles.additionalSection}>
            <Popover
              trigger={PopoverTrigger.HOVER}
              content={getMoreOptions({
                handleEditAction: handleEditFormOpen,
                handleNavigateToWebsite: () => {
                  handleNavigateWebsite(customerDetails?.website);
                },
              })}
              placement={PlacementPositions.BOTTOM_RIGHT}
            >
              <div className={styles.moreOptions}>
                <img src={ellipsisIcon} />
              </div>
            </Popover>
          </div>
        </div>
        <div className={styles.bottomSection}>
          <div
            className={styles.notesToggle}
            onClick={(e) => {
              e.stopPropagation();
              handleNotesModalOpen();
            }}
          >
            <img src={notesIcon} alt={NOTES_ALT} />
          </div>
           <div className={styles.notesContent}>
              {customerDetails?.notes}
            </div>
        </div>
      </div>
    </>
  );
};

export default CustomerDetailsCard;
